import { TableComponent as Table } from '../../components'
import { Input, Button, Tag, Space, Tooltip } from 'antd'
import { SearchOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons'
import moment from 'moment'
import { maskCurrency } from '../../utils/functions'

const CommissionsTable = (props) => {
    const { data, loading, showDetailsModal, handleApprove, handleReject, refreshList } = props
    
    // Status tags with appropriate colors
    const renderStatus = (status) => {
        if (status === 'pending') {
            return <Tag color="orange">Pendente</Tag>
        } else if (status === 'approved') {
            return <Tag color="green">Aprovado</Tag>
        } else if (status === 'rejected') {
            return <Tag color="red">Rejeitado</Tag>
        }
        return <Tag>{status}</Tag>
    }
    
    // Create column definitions for the table
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            align: 'center',
            width: '5%',
            sorter: {
                compare: (a, b) => a.id - b.id,
                multiple: 1
            }
        },
        {
            title: 'Contrato',
            dataIndex: 'contractId',
            key: 'contractId',
            align: 'center',
            width: '10%',
            sorter: {
                compare: (a, b) => a.contractId - b.contractId,
                multiple: 2
            }
        },
        {
            title: 'Referenciador',
            dataIndex: 'referrer',
            key: 'referrer',
            align: 'center',
            width: '15%',
            render: referrer => referrer?.nome || '-',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Buscar referenciador"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Buscar
                        </Button>
                        <Button
                            onClick={() => clearFilters()}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value, record) => record.referrer?.nome?.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Cliente',
            dataIndex: 'client',
            key: 'client',
            align: 'center',
            width: '15%',
            render: client => client?.nome || '-',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Buscar cliente"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Buscar
                        </Button>
                        <Button
                            onClick={() => clearFilters()}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value, record) => record.client?.nome?.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Nível',
            dataIndex: 'level',
            key: 'level',
            align: 'center',
            width: '5%',
            filters: [
                { text: 'Nível 1', value: 1 },
                { text: 'Nível 2', value: 2 },
                { text: 'Nível 3', value: 3 }
            ],
            onFilter: (value, record) => record.level === value,
        },
        {
            title: 'Porcentagem',
            dataIndex: 'percentage',
            key: 'percentage',
            align: 'center',
            width: '10%',
            render: percentage => {
                // Handle percentage as either string or number
                const percentValue = typeof percentage === 'string' ? parseFloat(percentage) : percentage;
                return `${percentValue.toFixed(2)}%`;
            },
            sorter: {
                compare: (a, b) => {
                    // Handle sorting for both string and number types
                    const aValue = typeof a.percentage === 'string' ? parseFloat(a.percentage) : a.percentage;
                    const bValue = typeof b.percentage === 'string' ? parseFloat(b.percentage) : b.percentage;
                    return aValue - bValue;
                },
                multiple: 3
            },
        },
        {
            title: 'Valor do Contrato',
            dataIndex: 'contractValue',
            key: 'contractValue',
            align: 'center',
            width: '10%',
            render: value => `R$ ${maskCurrency(value)}`,
            sorter: {
                compare: (a, b) => {
                    // Handle sorting for both string and number types
                    const aValue = typeof a.contractValue === 'string' ? parseFloat(a.contractValue) : a.contractValue;
                    const bValue = typeof b.contractValue === 'string' ? parseFloat(b.contractValue) : b.contractValue;
                    return aValue - bValue;
                },
                multiple: 4
            },
        },
        {
            title: 'Valor da Comissão',
            dataIndex: 'commissionValue',
            key: 'commissionValue',
            align: 'center',
            width: '10%',
            render: value => `R$ ${maskCurrency(value)}`,
            sorter: {
                compare: (a, b) => {
                    // Handle sorting for both string and number types
                    const aValue = typeof a.commissionValue === 'string' ? parseFloat(a.commissionValue) : a.commissionValue;
                    const bValue = typeof b.commissionValue === 'string' ? parseFloat(b.commissionValue) : b.commissionValue;
                    return aValue - bValue;
                },
                multiple: 5
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: '10%',
            render: renderStatus,
            filters: [
                { text: 'Pendente', value: 'pending' },
                { text: 'Aprovado', value: 'approved' },
                { text: 'Rejeitado', value: 'rejected' }
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Data de Criação',
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center',
            width: '10%',
            render: date => moment(date).format('DD/MM/YYYY'),
            sorter: {
                compare: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
                multiple: 6
            },
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center',
            width: '10%',
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title="Detalhes">
                        <Button 
                            type="primary" 
                            shape="circle" 
                            onClick={(e) => {
                                e.stopPropagation();
                                showDetailsModal(record.id);
                            }}
                            className="btn-white-icon"
                            icon={
                                <svg viewBox="64 64 896 896" focusable="false" data-icon="eye" width="1em" height="1em" fill="#ffffff" aria-hidden="true" style={{fill: '#ffffff', pointerEvents: 'none'}}>
                                    <path d="M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 000 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z" fill="#ffffff" style={{fill: '#ffffff', pointerEvents: 'none'}} />
                                </svg>
                            }
                            style={{ backgroundColor: '#023047', borderColor: '#023047' }}
                        />
                    </Tooltip>
                    
                    {record.status === 'pending' && (
                        <>
                            <Tooltip title="Aprovar">
                                <Button 
                                    type="primary" 
                                    shape="circle" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleApprove(record.id);
                                    }}
                                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                    className="btn-white-icon"
                                    icon={
                                        <svg viewBox="64 64 896 896" focusable="false" data-icon="check" width="1em" height="1em" fill="#ffffff" aria-hidden="true" style={{fill: '#ffffff', pointerEvents: 'none'}}>
                                            <path d="M912 190h-69.9c-9.8 0-19.1 4.5-25.1 12.2L404.7 724.5 207 474a32 32 0 00-25.1-12.2H112c-6.7 0-10.4 7.7-6.3 12.9l273.9 347c12.8 16.2 37.4 16.2 50.3 0l488.4-618.9c4.1-5.1.4-12.8-6.3-12.8z" fill="#ffffff" style={{fill: '#ffffff', pointerEvents: 'none'}} />
                                        </svg>
                                    }
                                />
                            </Tooltip>
                            <Tooltip title="Rejeitar">
                                <Button 
                                    type="primary" 
                                    shape="circle" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleReject(record.id);
                                    }}
                                    danger
                                    className="btn-white-icon"
                                    icon={
                                        <svg viewBox="64 64 896 896" focusable="false" data-icon="close" width="1em" height="1em" fill="#ffffff" aria-hidden="true" style={{fill: '#ffffff', pointerEvents: 'none'}}>
                                            <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" fill="#ffffff" style={{fill: '#ffffff', pointerEvents: 'none'}} />
                                        </svg>
                                    }
                                />
                            </Tooltip>
                        </>
                    )}
                </Space>
            ),
        },
    ]
    
    // Title component with refresh button
    const TableTitle = () => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Comissões</span>
            <Button 
                icon={<ReloadOutlined />} 
                onClick={refreshList}
                type="primary"
            >
                Atualizar
            </Button>
        </div>
    )
    
    return (
        <Table 
            loading={loading}
            column={columns} 
            data={data} 
            layout="fixed"
        />
    )
}

export default CommissionsTable 